/**
 * HTTP surface of the administration screens.
 *
 * Every call maps one-to-one onto a route that exists in
 * `apps/api/src/modules/settings`, `modules/users`, `modules/audit`,
 * `modules/catalog` (the SEC-02 category picker) and `modules/files` (the
 * clinic logo). Nothing here is invented.
 */
import type { Paginated } from '@nova/shared';
import { api } from '@/lib/api';
import type {
  AuditEntityOption,
  AuditLogRow,
  AuditQuery,
  CalendarSettings,
  CalendarSettingsPatch,
  ClinicIdentitySettings,
  ClinicSettings,
  ClinicSettingsPatch,
  CreateUserBody,
  EInvoiceSubmissionDetail,
  EInvoiceSubmissionQuery,
  EInvoiceSubmissionRow,
  FileAsset,
  Holiday,
  HolidayBody,
  HolidayQuery,
  PermissionCatalog,
  ProcedureCategoryNode,
  ReferenceItem,
  ReferenceItemBody,
  ReferenceListMeta,
  ReferenceListQuery,
  Role,
  RoleBody,
  RoleQuery,
  UpdateUserBody,
  UserDetail,
  UserQuery,
  UserRow,
  UserWithTemporaryPassword,
} from './types';

// ---------------------------------------------------------------------------
// Clinic + calendar settings
// ---------------------------------------------------------------------------

export const settingsApi = {
  get: () => api.get<ClinicSettings>('/settings').then((r) => r.data),
  update: (body: ClinicSettingsPatch) =>
    api.patch<ClinicSettings>('/settings', body).then((r) => r.data),

  /**
   * The non-sensitive slice of §5.10 that every signed-in screen may read —
   * `GET /settings` itself sits behind SETTINGS_MANAGE. Only the clinic identity
   * is typed here; the rest of the payload belongs to the screens that ask for it.
   */
  clientConfig: () =>
    api
      .get<{ clinic?: Partial<ClinicIdentitySettings> }>('/settings/client-config')
      .then((r) => r.data),

  getCalendar: () => api.get<CalendarSettings>('/settings/calendar').then((r) => r.data),
  updateCalendar: (body: CalendarSettingsPatch) =>
    api.patch<CalendarSettings>('/settings/calendar', body).then((r) => r.data),
};

// ---------------------------------------------------------------------------
// JoFotara submission log (BIL-10)
// ---------------------------------------------------------------------------

export const jofotaraApi = {
  /** سجل الإرسال — one row per attempt, newest first, blobs omitted. */
  submissions: (params: EInvoiceSubmissionQuery) =>
    api
      .get<Paginated<EInvoiceSubmissionRow>>('/einvoice/submissions', { params })
      .then((r) => r.data),
  /** The full attempt: the exact XML sent and the government's response. */
  submission: (id: string) =>
    api.get<EInvoiceSubmissionDetail>(`/einvoice/submissions/${id}`).then((r) => r.data),
};

export const holidaysApi = {
  list: (params: HolidayQuery) =>
    api.get<Paginated<Holiday>>('/holidays', { params }).then((r) => r.data),
  create: (body: HolidayBody) => api.post<Holiday>('/holidays', body).then((r) => r.data),
  update: (id: string, body: Partial<HolidayBody>) =>
    api.patch<Holiday>(`/holidays/${id}`, body).then((r) => r.data),
  /** CAL-12 — deactivates; the row stays to explain a past clinic closure. */
  deactivate: (id: string) => api.delete<Holiday>(`/holidays/${id}`).then((r) => r.data),
};

// ---------------------------------------------------------------------------
// Reference lists (PRD §5.10)
// ---------------------------------------------------------------------------

export const referenceListsApi = {
  keys: () => api.get<ReferenceListMeta[]>('/reference-lists').then((r) => r.data),
  list: (listKey: string, params: ReferenceListQuery) =>
    api.get<Paginated<ReferenceItem>>(`/reference-lists/${listKey}`, { params }).then((r) => r.data),
  create: (listKey: string, body: ReferenceItemBody) =>
    api.post<ReferenceItem>(`/reference-lists/${listKey}`, body).then((r) => r.data),
  update: (listKey: string, id: string, body: ReferenceItemBody) =>
    api.patch<ReferenceItem>(`/reference-lists/${listKey}/${id}`, body).then((r) => r.data),
  reorder: (listKey: string, orderedIds: string[]) =>
    api
      .post<ReferenceItem[]>(`/reference-lists/${listKey}/reorder`, { orderedIds })
      .then((r) => r.data),
  /** Deletes only when nothing references the row; otherwise deactivates it. */
  deactivate: (listKey: string, id: string) =>
    api
      .delete<{ deleted: boolean; item: ReferenceItem | null }>(`/reference-lists/${listKey}/${id}`)
      .then((r) => r.data),
};

// ---------------------------------------------------------------------------
// Users and roles (SEC-01 / SEC-05)
// ---------------------------------------------------------------------------

export const usersApi = {
  list: (params: UserQuery) =>
    api.get<Paginated<UserRow>>('/users', { params }).then((r) => r.data),
  get: (id: string) => api.get<UserDetail>(`/users/${id}`).then((r) => r.data),
  create: (body: CreateUserBody) =>
    api.post<UserWithTemporaryPassword>('/users', body).then((r) => r.data),
  update: (id: string, body: UpdateUserBody) =>
    api.patch<UserDetail>(`/users/${id}`, body).then((r) => r.data),
  resetPassword: (id: string, body: { newPassword?: string; mustChangePassword?: boolean }) =>
    api
      .post<{ user: UserDetail; temporaryPassword: string | null }>(
        `/users/${id}/reset-password`,
        body,
      )
      .then((r) => r.data),
  activate: (id: string) => api.post<UserDetail>(`/users/${id}/activate`, {}).then((r) => r.data),
  deactivate: (id: string, reason?: string) =>
    api.post<UserDetail>(`/users/${id}/deactivate`, { reason }).then((r) => r.data),
};

export const rolesApi = {
  permissions: () => api.get<PermissionCatalog>('/roles/permissions').then((r) => r.data),
  list: (params: RoleQuery) => api.get<Paginated<Role>>('/roles', { params }).then((r) => r.data),
  get: (id: string) => api.get<Role>(`/roles/${id}`).then((r) => r.data),
  create: (body: RoleBody) => api.post<Role>('/roles', body).then((r) => r.data),
  update: (id: string, body: RoleBody) =>
    api.patch<Role>(`/roles/${id}`, body).then((r) => r.data),
  remove: (id: string) => api.delete<{ id: string }>(`/roles/${id}`).then((r) => r.data),
};

/** SEC-02 — the categories a role's clinical view can be narrowed to. */
export const procedureCategoriesApi = {
  tree: () =>
    api
      .get<ProcedureCategoryNode[]>('/procedure-categories/tree', { params: { isActive: 'all' } })
      .then((r) => r.data),
};

// ---------------------------------------------------------------------------
// Audit log (SEC-04)
// ---------------------------------------------------------------------------

export const auditApi = {
  list: (params: AuditQuery) =>
    api.get<Paginated<AuditLogRow>>('/audit', { params }).then((r) => r.data),
  entities: () => api.get<AuditEntityOption[]>('/audit/entities').then((r) => r.data),
};

// ---------------------------------------------------------------------------
// Files — clinic logo only
// ---------------------------------------------------------------------------

export const filesApi = {
  upload: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    form.append('entityType', 'ClinicSetting');
    return api
      .post<FileAsset>('/files', form, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then((r) => r.data);
  },
  /**
   * `GET /files/:id` is bearer-guarded, so the logo cannot go straight into an
   * `<img src>` — it is fetched through the shared client and rendered from a
   * blob URL.
   */
  blobUrl: (id: string) =>
    api
      .get<Blob>(`/files/${id}`, { responseType: 'blob' })
      .then((r) => URL.createObjectURL(r.data)),
};

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const settingsKeys = {
  all: ['settings'] as const,
  clinic: () => [...settingsKeys.all, 'clinic'] as const,
  clientConfig: () => [...settingsKeys.all, 'client-config'] as const,
  calendar: () => [...settingsKeys.all, 'calendar'] as const,
  logo: (fileId: string) => [...settingsKeys.all, 'logo', fileId] as const,
};

export const jofotaraKeys = {
  all: ['einvoice-submissions'] as const,
  list: (params: EInvoiceSubmissionQuery) => [...jofotaraKeys.all, 'list', params] as const,
  detail: (id: string) => [...jofotaraKeys.all, 'detail', id] as const,
};

export const holidayKeys = {
  all: ['holidays'] as const,
  list: (params: HolidayQuery) => [...holidayKeys.all, 'list', params] as const,
};

export const referenceListKeys = {
  all: ['reference-lists'] as const,
  keys: () => [...referenceListKeys.all, 'keys'] as const,
  list: (listKey: string) => [...referenceListKeys.all, 'list', listKey] as const,
};

export const userKeys = {
  all: ['users'] as const,
  list: (params: UserQuery) => [...userKeys.all, 'list', params] as const,
  detail: (id: string) => [...userKeys.all, 'detail', id] as const,
};

export const roleKeys = {
  all: ['roles'] as const,
  permissions: () => [...roleKeys.all, 'permissions'] as const,
  list: (params: RoleQuery) => [...roleKeys.all, 'list', params] as const,
  detail: (id: string) => [...roleKeys.all, 'detail', id] as const,
};

export const procedureCategoryKeys = {
  all: ['procedure-categories'] as const,
  tree: () => [...procedureCategoryKeys.all, 'tree'] as const,
};

export const auditKeys = {
  all: ['audit'] as const,
  list: (params: AuditQuery) => [...auditKeys.all, 'list', params] as const,
  /** Distinct entities present in the log — powers the entity filter. */
  entities: () => [...auditKeys.all, 'entities'] as const,
};
